import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { PdfViewerComponent } from "ng2-pdf-viewer";
import { FaxStatus } from "src/app/enums/fax-status";
import { IFaxFolder } from "src/app/models/fax/fax-folder";
import { FaxService } from "src/app/services/fax.service";
import { ToastService } from "src/app/services/toast.service";

@Component({
  selector: "app-fax-pdf-viewer",
  templateUrl: "./fax-pdf-viewer.component.html",
  styleUrls: ["./fax-pdf-viewer.component.scss"],
})
export class FaxPdfViewerComponent implements OnInit {
  @Input() faxId: number | null = null;
  @Input() faxFileSrc = "";
  @Input() faxFilePage = 1;
  @Input() faxStatus = FaxStatus.Processing;
  @Input() fileName = "fax.pdf";
  @Input() showControls = true;

  @Output() fullscreenClick = new EventEmitter<void>();
  @Output() deleteClick = new EventEmitter<void>();
  @Output() openDetailedInfoClick = new EventEmitter<void>();
  @Output() resendClick = new EventEmitter<void>();
  @Output() emailClick = new EventEmitter<void>();

  @ViewChild(PdfViewerComponent, { static: false })
  private pdfComponent: PdfViewerComponent;

  selectedFolderName = "";
  pdfRotationDeg = 0;

  readonly controlGroups: IFaxViewerControlButton[][] = [
    [
      {
        imgSrc: "assets/icons/rotate-left.svg",
        imgAlt: "Rotate document to the left",
        onClick: () => this.rotate(-90),
      },
      {
        imgSrc: "assets/icons/rotate-right.svg",
        imgAlt: "Rotate document to the right",
        onClick: () => this.rotate(90),
      },
    ],
    [
      {
        imgSrc: "assets/icons/fullscreen.svg",
        imgAlt: "Open document in fullscreen mode",
        onClick: () => this.fullscreenClick.emit(),
      },
    ],
    [
      {
        imgSrc: "assets/icons/download-orange.svg",
        imgAlt: "Download document",
        onClick: () => this.downloadDocument(),
      },
      {
        imgSrc: "assets/icons/print.svg",
        imgAlt: "Print document",
        onClick: () => this.printDocument(),
      },
      {
        imgSrc: "assets/icons/mail-orange.svg",
        imgAlt: "Send document copy to email",
        onClick: () => this.emailClick.emit(),
      },
      {
        imgSrc: "assets/icons/delete-orange.svg",
        imgAlt: "Delete document",
        onClick: () => this.deleteClick.emit(),
      },
    ],
    [
      {
        imgSrc: "assets/icons/info.svg",
        imgAlt: "Open detailed document info",
        onClick: () => this.openDetailedInfoClick.emit(),
      },
    ],
  ];

  private folders: IFaxFolder[] = [];
  private foldersPageNumber = "1";

  private readonly _pageSize = "10";

  constructor(private readonly _faxService: FaxService, private readonly _toastService: ToastService) {}

  get folderSelectOptions() {
    return this.folders.map(f => f.name);
  }

  get showResendButton() {
    return this.faxStatus === FaxStatus.Failed;
  }

  rotate(deg: -90 | 90) {
    this.pdfRotationDeg += deg;
  }

  selectFolderName(folderName: string) {
    this.selectedFolderName = folderName;
  }

  saveToFolder() {
    if (this.faxId === null || !this.selectedFolderName) {
      return;
    }

    this._faxService.saveToFolder(this.faxId.toString(), this.selectedFolderName).subscribe({
      next: () => {
        this._toastService.callToast(`Fax saved to "${this.selectedFolderName}" folder`);
      },
      error: () => {
        this._toastService.callToast("Failed to save fax to folder");
      },
    });
  }

  isHorizontalViewer() {
    return this.pdfRotationDeg % 180 !== 0;
  }

  setViewerScaleToPageHeight() {
    this.pdfComponent.pdfViewer.currentScaleValue = "page-height";
  }

  downloadDocument() {
    if (!this.faxFileSrc) {
      return;
    }

    const link = document.createElement("a");
    link.href = this.faxFileSrc;
    link.download = this.fileName;
    link.click();
  }

  printDocument() {
    if (!this.faxFileSrc) {
      return;
    }

    const printWindow = window.open(this.faxFileSrc, "_blank");

    printWindow?.addEventListener("load", () => printWindow.print());
  }

  ngOnInit() {
    this.getFolders();
  }

  private getFolders() {
    this._faxService
      .getFolders({ pageNumber: this.foldersPageNumber, pageSize: this._pageSize })
      .subscribe(folders => {
        this.folders = folders.records;
      });
  }
}

interface IFaxViewerControlButton {
  imgSrc: string;
  imgAlt: string;
  onClick: () => void;
}
